import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { UtilsService } from 'src/app/shared/utils.service';
import { UserPublicProfileComponent } from '../../shared/user-public-profile/user-public-profile.component';
import { ApiDataSource } from '../../utils/api-data-source';
import { ApiRequestService } from '../../shared/api-request.service';
import { NetworkedUsersSelectorComponent } from '../../shared/networked-users-selector/networked-users-selector.component';
import { RBACService } from '../../shared/rbac.service';

@Component({
  selector: 'app-sites-users-worked-on',
  templateUrl: './sites-users-worked-on.component.html',
  styleUrls: ['./sites-users-worked-on.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesUsersWorkedOnComponent implements OnInit, AfterViewInit {
  // The site id to query.
  parent_id: number;
  child_id: number;
  site_id: number;

  // RBAC permission flags: resolved once in ngOnInit().
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;
  canSendMessage: boolean = false;

  // Build displayedColumns dynamically: NOT as a static array.
  displayedColumns: string[] = [];

  // The data source object.
  dataSource: SitesUsersWorkedOnDataSource;

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    // public service: ContractorsService,
    public app: AppService,
    public utils: UtilsService,
    public route: ActivatedRoute,
    private api: ApiRequestService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_USERS_WORKED_ON,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_USERS_WORKED_ON,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_USERS_WORKED_ON,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );
    this.canSendMessage = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_MESSAGES,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access users worked on.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = ['contact_person', 'email', 'mobile', 'trade'];
    if (this.canView || this.canDelete) {
      this.displayedColumns.unshift('select');
    }
    if (this.canEdit || this.canDelete) {
      this.displayedColumns.push('actions');
    }

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;
      // load a multi select data source object
      this.dataSource = new SitesUsersWorkedOnDataSource(
        this.app,
        this.api,
        true
      );
      this.dataSource.site_id = this.site_id;
      // get the data only when the site id is present and user has view permission
      if (this.canView) {
        this.dataSource.getData();
      }
    }
  }

  ngAfterViewInit() {
    // Reset the paginator when soring records.
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      this.dataSource.getData(true);
    });

    // Subscribe to the paginator and sorting tap events.
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   *
   * @param id the contractor id
   *
   * unlink a single contractor
   */
  onRemove(user_id: number) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink users from this site.'
      );
      return;
    }
    this.removeUsers([user_id]);
  }

  /**
   * Remove selected users from the list.
   */
  onRemoveSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to unlink users from this site.'
      );
      return;
    }
    this.removeUsers(this.dataSource.selection.selected);
  }

  /**
   * Open a dialog to allow the admin user to select connected users to add to the list.
   */
  onAddUsers() {
    if (!this.canEdit) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to add users to this site.'
      );
      return;
    }
    this.utils
      .showComponentDialog(
        NetworkedUsersSelectorComponent,
        {
          multiple: true,
          visitor_site_id: this.site_id
        },
        {
          width: '1200px'
        }
      )
      .then((user_ids: number[]) => {
        // Do nothing if the response is undefined.
        if (typeof user_ids === 'undefined') {
          return;
        }
        // Do nothing if no users were selected.
        if (!user_ids || user_ids.length == 0) {
          return;
        }
        // Make an API request to add the users.
        this.api
          .makeRequest('put', `v2/sites/${this.site_id}/users-worked-on`, {
            user_ids: user_ids
          })
          .then((response) => {
            // Refresh the list.
            this.dataSource.getData(true);
            // Show toast message
            this.utils.showToast(response.message);
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      });
  }

  /**
   * Remove users from the list.
   * @param user_ids
   * @private
   */
  private removeUsers(user_ids: number[]) {
    // Terminate early if no site id is present.
    if (!this.site_id) {
      return;
    }
    // Check if there are any users to remove.
    if (user_ids.length === 0) {
      this.utils.showModal(
        'Select Users',
        'You need to select some users before they can be removed.'
      );
      return;
    }
    // Get Confirmation
    this.utils.showModal(
      'Remove User(s)',
      `Are you sure you want to remove ${user_ids.length} user(s)?`,
      () => {
        // Make the API request to remove the users.
        this.api
          .makeRequest(
            'delete',
            `v2/sites/${this.site_id}/users-worked-on`,
            {},
            {
              user_ids: user_ids.join(',')
            }
          )
          .then((response) => {
            // Refresh the list.
            this.dataSource.getData(true);
            // Show toast message
            this.utils.showToast(`${user_ids.length} User(s) were removed.`);
            // Clear selected items.
            this.dataSource.selection.deselect(...user_ids);
          })
          .catch((errorResponse) => {
            this.utils.handleAPIErrors(errorResponse);
          });
      }
    );
  }

  /**
   * Send a new message to selected users.
   */
  composeNewMessage() {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to send messages to users on this site.'
      );
      return;
    }
    if (!this.canSendMessage) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to send messages.'
      );
      return;
    }
    // Terminate early if no users were selected.
    if (this.dataSource.selection.selected.length == 0) {
      this.utils.showToast('Please select some users to send the message to.');
      return;
    }
    // Open the message editor with the users pre-selected.
    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectUserIds: this.dataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then((response) => {
        if (response) {
          this.utils.showToast(
            'You can view the message stats in the messages section.'
          );
        }
      });
  }

  /**
   * View the user's public profile.
   * @param hash
   */
  onUserPublicView(hash: string) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view user profiles.'
      );
      return;
    }
    this.utils.showComponentDialog(UserPublicProfileComponent, hash, {
      width: '90%'
    });
  }

  /**
   * Export a list of users that worked on the site.
   */
  onExport() {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to export users worked on data.'
      );
      return;
    }
    this.api
      .makeDownloadRequest(
        `v2/sites/${this.site_id}/users-worked-on`,
        {},
        {
          ids: this.dataSource.selection.selected.join(','),
          export: 'csv'
        }
      )
      .then((response) => {
        // Workaround for IE - No longer supported
        // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        //   window.navigator.msSaveOrOpenBlob(response);
        //   return;
        // }

        // Get the current date object
        const date = new Date();

        // Create object url to handle file downloads
        const data = window.URL.createObjectURL(response);

        // Create a download link
        const downloadLink = document.createElement('a');
        downloadLink.href = data;
        downloadLink.download = `Users - ${date.getFullYear()} / ${date.getMonth() +
          1} / ${date.getDate()}.csv`;
        // Initiate the download
        downloadLink.click();

        // For FireFox it is necessary to delay revoking the ObjectURL
        setTimeout(function() {
          window.URL.revokeObjectURL(data);
        }, 300); // Minimum 300 milliseconds.
      })
      .catch((errorResponse) => {
        this.utils.showModal('Error', errorResponse.message);
      });
  }

  // Expose RBACService to template for static member access.
  protected readonly RBACService = RBACService;
}

export class SitesUsersWorkedOnDataSource extends ApiDataSource {
  // The site id to filter records by.
  site_id: number = 0;

  order_by: string = 'contact_person';
  order: string = 'asc';

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/sites/${this.site_id}/users-worked-on`, resetOffset);
  }
}
